/**
 * The brand mark and the wordmark lockup — adopted 1 October 2026 from the
 * "common thread" reference (`.superpowers/plans/brand-reference/assets/mark.svg`).
 *
 * The mark is one stroke: an `n` that runs on into a loop and back — the thread
 * the brand is named for. It is drawn inline rather than loaded as an `<img>` so
 * it takes `currentColor`: the colour comes from a token on the parent, which is
 * what lets it follow dark mode. The favicon is the one copy that cannot do
 * this (see `public/favicon.svg`).
 *
 * Both are **decorative** (`aria-hidden`). The accessible name belongs to
 * whatever wraps them — the header's home link carries `brand.name` from the
 * locale — because rule 1 of this directory is that no text lives in a
 * component, and a mark that named itself would be read twice inside a link
 * that already does.
 */
import { cn } from './cn'

/** The reference's path, verbatim. 110×75, drawn on a 13-unit round stroke. */
const MARK_PATH =
  'M10 62V31C10 7 35 7 45 30L54 48C64 72 99 68 99 40C99 14 70 4 64 24C59 40 75 47 83 38'

export type BrandMarkProps = { className?: string | undefined }

/**
 * The thread mark alone. Size it with a height (`h-7`); the width follows the
 * 110:75 viewBox. Colour it with a text token — `text-brand-fg` is the default
 * because that is the wine that stays visible on the dark ground (600 is a fill
 * there, 2.47:1 against the page, and the mark would all but disappear).
 */
export function BrandMark({ className }: BrandMarkProps) {
  return (
    <svg
      viewBox="0 0 110 75"
      aria-hidden="true"
      focusable="false"
      className={cn('h-7 w-auto shrink-0 text-brand-fg', className)}
    >
      <path
        d={MARK_PATH}
        fill="none"
        stroke="currentColor"
        strokeWidth="13"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export type BrandLockupProps = {
  /** The wordmark text — `brand.wordmark` from the `common` namespace. */
  wordmark: string
  size?: 'md' | 'lg' | undefined
  className?: string | undefined
}

/**
 * Mark + wordmark, as the reference's header and footer set them.
 *
 * ⚠️ **`dir="ltr"` and `lang="en"` on purpose, in both languages.** The
 * wordmark is a Latin logotype, not a translated string: in an Arabic page it
 * still reads mark-then-name from the left, as the reference does
 * (`html[dir=rtl] .brand { direction: ltr }`). `lang="en"` is what keeps it in
 * the Georgia serif — `font-display` re-points to the Arabic face under
 * `:lang(ar)` (index.css), and without this the logotype would change typeface
 * when the user switched language. It is also what makes the negative tracking
 * safe: tracking is only ever applied to Latin.
 */
export function BrandLockup({ wordmark, size = 'md', className }: BrandLockupProps) {
  return (
    <span
      dir="ltr"
      lang="en"
      aria-hidden="true"
      className={cn('inline-flex shrink-0 items-center gap-1.5 text-fg sm:gap-2.5', className)}
    >
      {/* The reference draws the mark as a 46×36 image; the 110:75 path sits
          inside that box at its own aspect, which is what `w-[46px] h-9`
          reproduces (the SVG letterboxes rather than stretching). */}
      <BrandMark
        className={
          size === 'lg'
            ? 'h-9 w-[46px]'
            : 'h-6 w-8 min-[360px]:h-7 min-[360px]:w-9 md:h-9 md:w-[46px]'
        }
      />
      <span
        className={cn(
          // The reference: Georgia 600 at 36px (30px in the footer and on a
          // phone), -2px tracking (-0.055em), 0.9 leading. Gelasio 600 is
          // self-hosted for exactly this (index.css).
          'font-display leading-[0.9] font-semibold tracking-[-0.055em]',
          // ⚠️ Smaller below `md`, measured at 390, 375 and 320px in Arabic,
          // where "تسجيل الدخول" is the widest thing in the row: the
          // reference's 30px phone size does not leave the sign-in room beside
          // the menu button this header has and the reference does not.
          size === 'lg'
            ? 'text-[1.875rem]'
            : 'text-[1.25rem] min-[360px]:text-[1.625rem] md:text-[2.25rem]',
        )}
      >
        {wordmark}
      </span>
    </span>
  )
}
